import React from "react";
import { ShoppingBag, Trash2, Plus, Minus, ShieldCheck, X, ArrowRight } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useCart } from "@/context/CartContext";

const PaymentLogos = () => (
  <div className="flex items-center gap-3 flex-wrap">
    <span className="px-3 py-1.5 rounded-md bg-white border border-gold/20 text-[13px] font-bold text-[#1A1F71] tracking-wide">VISA</span>
    <span className="px-3 py-1.5 rounded-md bg-white border border-gold/20 text-[13px] font-bold tracking-wide">
      <span className="text-[#EB001B]">Master</span><span className="text-[#F79E1B]">card</span>
    </span>
    <span className="px-3 py-1.5 rounded-md bg-white border border-gold/20 text-[13px] font-bold tracking-wide">
      <span className="text-[#003087]">Pay</span><span className="text-[#009cde]">Pal</span>
    </span>
  </div>
);

export const CartPanel = () => {
  const { items, removeItem, updateQty, total, isOpen, closeCart, checkoutConfig } = useCart();
  const navigate = useNavigate();
  const onsite = checkoutConfig?.onsite_checkout_enabled === true;

  if (!isOpen) return null;

  const goCheckout = () => {
    if (items.length === 0) return;
    closeCart();
    navigate("/checkout");
  };

  return (
    <>
      <div
        data-testid="cart-overlay"
        className={`fixed inset-0 bg-brown-black/40 backdrop-blur-sm z-[90] transition-opacity duration-300 ${isOpen ? "opacity-100" : "opacity-0 pointer-events-none"}`}
        onClick={closeCart}
      />
      <aside
        className={`fixed top-0 right-0 h-full w-full max-w-md bg-ivory z-[95] shadow-2xl flex flex-col transition-transform duration-300 ${isOpen ? "translate-x-0" : "translate-x-full"}`}
        data-testid="cart-panel"
      >
        <div className="flex items-center justify-between px-6 py-5 border-b border-gold/20 bg-brown-dark text-ivory">
          <h3 className="font-heading text-2xl flex items-center gap-2"><ShoppingBag size={20} /> Votre panier</h3>
          <button onClick={closeCart} data-testid="cart-close-button" className="p-1.5 hover:text-gold-light"><X size={22} /></button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-5">
          {items.length === 0 ? (
            <div className="text-center text-brown-black/50 mt-20" data-testid="cart-empty">
              <ShoppingBag size={48} className="mx-auto mb-4 text-gold/40" />
              <p>Votre panier est vide.</p>
              <button onClick={closeCart} data-testid="cart-empty-continue" className="mt-5 text-gold font-semibold hover:underline">Continuer mes achats</button>
            </div>
          ) : (
            <ul className="space-y-4">
              {items.map((i) => (
                <li key={i.key} className="flex gap-4 bg-white rounded-xl p-3 border border-gold/15" data-testid={`cart-item-${i.key}`}>
                  {i.image && <img src={i.image} alt={i.name} className="w-16 h-16 rounded-lg object-cover" />}
                  <div className="flex-1 min-w-0">
                    <p className="font-heading text-base font-medium text-brown-black truncate">{i.name}</p>
                    {i.variant_label && <p className="text-[11px] text-brown-black/50">{i.variant_label}</p>}
                    <p className="text-gold font-semibold text-sm">{i.price.toFixed(2)} €</p>
                    <div className="flex items-center gap-2 mt-1.5">
                      <button data-testid={`cart-decrease-${i.key}`} onClick={() => updateQty(i.key, i.qty - 1)} className="w-6 h-6 flex items-center justify-center rounded-full border border-gold/30 text-gold hover:bg-gold/10"><Minus size={12} /></button>
                      <span className="text-sm font-semibold w-5 text-center" data-testid={`cart-qty-${i.key}`}>{i.qty}</span>
                      <button data-testid={`cart-increase-${i.key}`} onClick={() => updateQty(i.key, i.qty + 1)} className="w-6 h-6 flex items-center justify-center rounded-full border border-gold/30 text-gold hover:bg-gold/10"><Plus size={12} /></button>
                    </div>
                  </div>
                  <button onClick={() => removeItem(i.key)} data-testid={`cart-remove-${i.key}`} className="text-brown-black/30 hover:text-red-500 transition-colors" aria-label="Supprimer"><Trash2 size={18} /></button>
                </li>
              ))}
            </ul>
          )}
        </div>

        {items.length > 0 && (
          <div className="border-t border-gold/20 px-6 py-5 bg-white space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-brown-black/70 font-medium">Sous-total</span>
              <span className="font-heading text-2xl font-semibold text-gold" data-testid="cart-total">{total.toFixed(2)} €</span>
            </div>
            {onsite && <p className="text-xs text-brown-black/50">Produits numériques — aucun frais de livraison. Téléchargement immédiat après paiement.</p>}
            <button onClick={goCheckout} data-testid="checkout-button" className="w-full inline-flex items-center justify-center gap-2 px-5 py-3.5 rounded-full bg-gold text-white font-semibold hover:bg-gold-light transition-colors">
              {onsite ? "Passer au paiement" : "Accéder aux achats"} <ArrowRight size={17} />
            </button>
            <button onClick={closeCart} data-testid="cart-continue-shopping" className="w-full text-brown-black/60 text-sm font-semibold hover:text-gold">Continuer mes achats</button>
            {onsite && <PaymentLogos />}
            {onsite && <p className="flex items-center gap-2 text-xs text-brown-black/55">
              <ShieldCheck size={14} className="text-gold" /> Paiement sécurisé via PayPal.
            </p>}
          </div>
        )}
      </aside>
    </>
  );
};

export default CartPanel;
